import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Slider,
  Button,
  Stack,
  Chip,
  Paper,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { submitJudgeScores } from "../../../api/competitionFinalNight";
import { goldScrollbarSx } from "../../../utils/finalNightScoring";

const gold = "#C9A66B";

function buildInitialScores(contestants) {
  const init = {};
  contestants.forEach((c) => {
    init[c.contestant_id] = c.my_judge_score ?? 5;
  });
  return init;
}

export default function JudgeScoringPanel({
  competitionId,
  roundId,
  roundName,
  contestants,
  token,
  onSubmitted,
}) {
  const [scores, setScores] = useState(() => buildInitialScores(contestants));
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const active = useMemo(
    () => contestants.filter((c) => !c.is_eliminated),
    [contestants]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return active;
    return active.filter((c) => {
      const name = String(c.full_name || "").toLowerCase();
      const category = String(c.category || "").toLowerCase();
      return name.includes(q) || category.includes(q);
    });
  }, [active, search]);

  useEffect(() => {
    setScores(buildInitialScores(contestants));
  }, [contestants, roundId]);

  const submitScores = async (items, successMessage) => {
    const res = await submitJudgeScores(
      {
        competition_id: competitionId,
        round_id: roundId,
        scores: items,
      },
      token
    );
    if (res.data.success) {
      toast.success(successMessage);
      onSubmitted?.();
      return true;
    }
    return false;
  };

  const handleSubmitOne = async (contestant) => {
    const contestantId = contestant.contestant_id;
    setSavingId(contestantId);
    try {
      await submitScores(
        [
          {
            contestant_id: contestantId,
            score: scores[contestantId] ?? 5,
          },
        ],
        `Score saved for ${contestant.full_name || "contestant"}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not submit score");
    } finally {
      setSavingId(null);
    }
  };

  const handleSubmitAll = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const payload = active.map((c) => ({
        contestant_id: c.contestant_id,
        score: scores[c.contestant_id] ?? 5,
      }));
      await submitScores(
        payload,
        `Scores submitted for all ${active.length} contestants`
      );
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not submit scores");
    } finally {
      setSaving(false);
    }
  };

  const searchActive = search.trim().length > 0;

  return (
    <Paper
      elevation={0}
      className="fn-judge-panel"
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${gold}44`,
        bgcolor: "#1a1d27",
        height: "fit-content",
        maxHeight: { lg: "calc(100vh - 48px)" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: `linear-gradient(135deg, ${gold}33 0%, transparent 60%), #242743`,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <GavelIcon sx={{ color: gold }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ color: gold, fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>
              JUDGE PANEL
            </Typography>
            <Typography sx={{ color: "#fff", fontWeight: 700 }}>
              {roundName} — score each contestant 1–10
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mt: 0.5 }}>
              {active.length} contestant{active.length === 1 ? "" : "s"} to score
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          p: 2.5,
          pb: 2,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or division…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "rgba(255,255,255,0.45)", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.04)",
              "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
              "&:hover fieldset": { borderColor: `${gold}66` },
              "&.Mui-focused fieldset": { borderColor: gold },
            },
          }}
        />

        {searchActive ? (
          <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 1.5 }}>
            Showing {filtered.length} of {active.length} contestants
          </Typography>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight: { xs: "min(50vh, 420px)", lg: "min(58vh, 480px)" },
            overflowY: "auto",
            pr: 0.5,
            ...goldScrollbarSx,
          }}
        >
          <Stack spacing={2}>
            {filtered.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
                  No contestants match your search.
                </Typography>
              </Box>
            ) : (
              filtered.map((c) => (
                <Box
                  key={c.contestant_id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                    <Avatar
                      src={c.profile_image}
                      sx={{ width: 48, height: 48, border: `2px solid ${gold}` }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 700 }} noWrap>
                        {c.full_name}
                      </Typography>
                      {c.category ? (
                        <Chip
                          size="small"
                          label={c.category}
                          sx={{
                            mt: 0.5,
                            height: 22,
                            fontSize: 11,
                            bgcolor: "rgba(201,166,107,0.15)",
                            color: gold,
                            maxWidth: "100%",
                          }}
                        />
                      ) : null}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 28,
                        fontWeight: 800,
                        color: gold,
                        minWidth: 36,
                        textAlign: "center",
                      }}
                    >
                      {scores[c.contestant_id]}
                    </Typography>
                  </Stack>
                  <Slider
                    value={scores[c.contestant_id] ?? 5}
                    min={1}
                    max={10}
                    step={1}
                    marks
                    disabled={saving || savingId === c.contestant_id}
                    onChange={(_, v) =>
                      setScores((prev) => ({ ...prev, [c.contestant_id]: v }))
                    }
                    sx={{
                      color: gold,
                      "& .MuiSlider-markLabel": { color: "rgba(255,255,255,0.5)" },
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={saving || savingId != null}
                    onClick={() => handleSubmitOne(c)}
                    sx={{
                      mt: 1,
                      borderColor: `${gold}88`,
                      color: gold,
                      fontWeight: 600,
                      "&:hover": { borderColor: gold, bgcolor: "rgba(201,166,107,0.08)" },
                    }}
                  >
                    {savingId === c.contestant_id ? "Saving…" : "Save this score"}
                  </Button>
                </Box>
              ))
            )}
          </Stack>
        </Box>

        <Button
          fullWidth
          variant="contained"
          disabled={saving || savingId != null || active.length === 0}
          onClick={() => setConfirmOpen(true)}
          sx={{
            mt: 2.5,
            py: 1.4,
            fontWeight: 700,
            bgcolor: gold,
            color: "#1a1d27",
            "&:hover": { bgcolor: "#dfc891" },
          }}
        >
          {saving ? "Submitting…" : `Submit all scores (${active.length})`}
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => !saving && setConfirmOpen(false)}
        PaperProps={{
          sx: { bgcolor: "#242743", color: "#fff", border: `1px solid ${gold}44`, minWidth: 320 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Submit all scores?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(255,255,255,0.75)" }}>
            You are about to submit scores for all{" "}
            <strong>{active.length}</strong> contestants in <strong>{roundName}</strong>.
            {searchActive ? (
              <>
                {" "}
                This includes everyone in the round, not only the{" "}
                {filtered.length} shown in your current search.
              </>
            ) : null}
            {" "}
            Please confirm your sliders are set correctly before continuing.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={saving}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAll}
            disabled={saving}
            sx={{ bgcolor: gold, color: "#1a1d27", fontWeight: 700, "&:hover": { bgcolor: "#dfc891" } }}
          >
            Yes, submit all
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
